import { Empty, Skeleton, Spin } from 'antd';
import { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { FileCard, GetFilesDataItem } from 'services';
import FilePicker from '../FilePicker';
import { Gallery } from './Gallery.component';
import { EmptyState } from './components/EmptyState';
import GalleryFilesHeader from './components/GalleryFilesHeader.component';

export interface FilesGalleryInputProps {
  value?: GetFilesDataItem[];
  onChange?: (value?: GetFilesDataItem[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
}

export const FilesGalleryInput: FC<FilesGalleryInputProps> = ({
  value,
  onChange = (_value?: GetFilesDataItem[]) => {},
  disabled,
  isLoading,
  tooltip,
}) => {
  const [filePickerVisible, setFilePickerVisible] = useState(false);
  const initialValues = useMemo(() => value, [value]);
  const hasFiles = useMemo(() => !!initialValues?.length, [initialValues]);

  const handleOnOkay = useCallback(
    (items: FileCard[]) => {
      onChange(items as GetFilesDataItem[]);
    },
    [onChange],
  );

  if (isLoading) {
    return <Skeleton.Button block active style={{ height: 80 }} />;
  }

  return (
    <div id="files-gallery-input">
      <GalleryFilesHeader
        disabled={disabled}
        openFilePicker={() => {
          setFilePickerVisible(true);
        }}
        hasFiles={hasFiles}
        tooltip={tooltip}
      />
      {hasFiles ? <Gallery files={initialValues} /> : <Empty description={<EmptyState />} />}
      {filePickerVisible && (
        <Suspense fallback={<Spin spinning fullscreen />}>
          <FilePicker
            title="Change gallery files"
            multiple
            initialSelectedFiles={(initialValues as GetFilesDataItem[]) || []}
            close={() => setFilePickerVisible(false)}
            onFinish={handleOnOkay}
            open={filePickerVisible}
          />
        </Suspense>
      )}
    </div>
  );
};
